import { FC, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Typography,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Input,
  toast,
  Card,
  CardContent,
  CardHeader,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconCheck, IconX, IconCopy } from "@tabler/icons-react";
import { PlaygroundAgentInfo } from "../types";
import { getValidModel } from "../utils";

interface PlaygroundAgentDetailsProps {
  agentDetails: PlaygroundAgentInfo;
  selectedAgentName: string;
  availableModels?: string[];
  onSaveAsLocal: (agentData: {
    name: string;
    description: string;
    prompt: string;
    model: string;
    source_type: "langfuse";
    source_id: string;
  }) => void;
}
interface AgentFormData {
  prompt: string;
  model: string;
  description: string;
  tags: string;
}

export const PlaygroundAgentDetails: FC<PlaygroundAgentDetailsProps> = ({
  agentDetails,
  selectedAgentName,
  availableModels,
  onSaveAsLocal,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const validModel = getValidModel(agentDetails.model, availableModels);

  const { register, handleSubmit, setValue, watch, reset } = useForm<AgentFormData>({
    defaultValues: {
      prompt: agentDetails.prompt,
      model: validModel,
      description: agentDetails.description,
      tags: agentDetails.tags?.join(", ") || "",
    },
  });

  useEffect(() => {
    reset({
      prompt: agentDetails.prompt,
      model: getValidModel(agentDetails.model, availableModels),
      description: agentDetails.description,
      tags: agentDetails.tags?.join(", ") || "",
    });
  }, [agentDetails, reset, availableModels]);

  const onSubmit = (data: AgentFormData) => {
    // Save edited Langfuse agent as local agent
    onSaveAsLocal({
      name: selectedAgentName,
      description: data.description,
      prompt: data.prompt,
      model: data.model,
      source_type: "langfuse",
      source_id: selectedAgentName,
    });

    setIsEditing(false);
    toast({
      description:
        "Agent saved locally successfully. This local version will be used instead of the Langfuse version.",
    });
  };
  const handleCancel = () => {
    setIsEditing(false);
    reset({
      prompt: agentDetails.prompt,
      model: agentDetails.model,
      description: agentDetails.description,
      tags: agentDetails.tags?.join(", ") || "",
    });
  };
  const currentModel = watch("model");

  return (
    <Card>
      <CardHeader>
        <Stack direction="row" justify="between" align="center">
          <div>
            <Stack direction="row" gap="sm" align="center">
              <Typography variant="body" weight="medium">
                {agentDetails.agent_name || selectedAgentName}
              </Typography>
              <Badge className="!al-bg-purple-100 !al-text-purple-700">Langfuse</Badge>
            </Stack>
          </div>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
              <IconCopy className="al-w-4 al-h-4 al-mr-2" />
              Save as Local
            </Button>
          )}
        </Stack>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="al-space-y-6">
          <div>
            <Typography
              variant="body"
              weight="medium"
              className="al-text-gray-700 al-uppercase al-mb-3"
            >
              Agent Name
            </Typography>
            <div className="al-bg-gray-50 al-border al-border-gray-200 al-p-3 al-rounded-lg">
              <Typography variant="body" weight="medium" className="al-text-gray-900">
                {agentDetails.agent_name || selectedAgentName}
              </Typography>
            </div>
          </div>
          <div>
            <Typography
              variant="body"
              weight="medium"
              className="al-text-gray-700 al-uppercase al-mb-3"
            >
              Description
            </Typography>
            {isEditing ? (
              <Textarea
                {...register("description")}
                rows={3}
                className="al-border-gray-300 al-bg-gray-50 focus:al-border-gray-400 focus:al-ring-gray-200"
              />
            ) : (
              <div className="al-bg-gray-50 al-border al-border-gray-200 al-p-3 al-rounded-lg">
                <Typography className="al-text-gray-800">{agentDetails.description}</Typography>
              </div>
            )}
          </div>
          <div>
            <Typography
              variant="body"
              weight="medium"
              className="al-text-gray-700 al-uppercase al-mb-3"
            >
              Version
            </Typography>
            <Stack direction="row" gap="sm" align="center">
              <Badge className="!al-bg-purple-100 !al-text-purple-700">
                Current: v{agentDetails.version}
              </Badge>
              {agentDetails.all_versions && agentDetails.all_versions.length > 1 && (
                <Typography className="al-text-gray-500">
                  All versions: {agentDetails.all_versions.join(", ")}
                </Typography>
              )}
            </Stack>
          </div>
          <div>
            <Typography
              variant="body"
              weight="medium"
              className="al-text-gray-700 al-uppercase al-mb-3"
            >
              Model
            </Typography>
            {isEditing ? (
              <Select value={currentModel} onValueChange={value => setValue("model", value)}>
                <SelectTrigger className="al-w-full">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {availableModels?.map((model: string) => (
                    <SelectItem key={model} value={model}>
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="al-bg-blue-50 al-border al-border-blue-200 al-p-3 al-rounded-lg">
                <Typography className="al-font-mono al-text-blue-900">{validModel}</Typography>
              </div>
            )}
          </div>
          {agentDetails.labels && agentDetails.labels.length > 0 && (
            <div>
              <Typography
                variant="body"
                weight="medium"
                className="al-text-gray-700 al-uppercase al-mb-3"
              >
                Labels
              </Typography>
              <Stack direction="row" className="al-flex-wrap al-gap-2">
                {agentDetails.labels.map((label: string, idx: number) => (
                  <Badge
                    key={idx}
                    className="!al-bg-cyan-100 !al-text-cyan-700 al-border al-border-cyan-200"
                  >
                    {label}
                  </Badge>
                ))}
              </Stack>
            </div>
          )}
          <div>
            <Typography
              variant="body"
              weight="medium"
              className="al-text-gray-700 al-uppercase al-mb-3"
            >
              Tags (Sub-agents)
            </Typography>
            {isEditing ? (
              <div className="al-space-y-2">
                <Input
                  {...register("tags")}
                  placeholder="Enter comma-separated agent names (e.g., agent1, agent2)"
                  className="al-border-gray-300 al-bg-gray-50 focus:al-border-gray-400 focus:al-ring-gray-200"
                />
                <Typography className="al-text-gray-500">
                  Enter sub-agent names separated by commas
                </Typography>
              </div>
            ) : (
              <Stack direction="row" className="al-flex-wrap al-gap-2">
                {agentDetails.tags && agentDetails.tags.length > 0 ? (
                  agentDetails.tags.map((tag: string, idx: number) => (
                    <Badge
                      key={idx}
                      className="!al-bg-gray-100 !al-text-gray-700 al-border al-border-gray-300"
                    >
                      {tag}
                    </Badge>
                  ))
                ) : (
                  <Typography className="al-text-gray-500 al-italic">
                    No sub-agents configured
                  </Typography>
                )}
              </Stack>
            )}
          </div>
          <div>
            <Typography
              variant="body"
              weight="medium"
              className="al-text-gray-700 al-uppercase al-mb-3"
            >
              System Prompt
            </Typography>
            {isEditing ? (
              <>
                <Textarea
                  {...register("prompt")}
                  rows={12}
                  className="al-font-mono al-text-sm al-border-blue-200 al-bg-blue-50 focus:al-border-blue-300 focus:al-ring-blue-100"
                />
                <Stack direction="row" gap="sm" justify="end" className="al-mt-3">
                  <Button onClick={handleCancel} variant="outline" size="sm" type="button">
                    <IconX className="al-w-4 al-h-4 al-mr-2" />
                    Cancel
                  </Button>
                  <Button type="submit" variant="default" size="sm">
                    <IconCheck className="al-w-4 al-h-4 al-mr-2" />
                    Save Locally
                  </Button>
                </Stack>
              </>
            ) : (
              <div className="al-bg-gray-50 al-border al-border-gray-200 al-p-4 al-rounded-lg al-max-h-96 al-overflow-y-auto">
                <pre className="al-text-xs al-whitespace-pre-wrap al-font-mono al-text-gray-800">
                  {agentDetails.prompt.replace(/#\$%/g, "\n\n\n")}
                </pre>
              </div>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
